import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import routes from "./routes";
import AuthRoute from "../../../../components/AuthRoute";
import { useMutation, useQuery } from "@apollo/client";
import { FETCH_ARTIST as fetchArtist } from "../../../../graphql/query/Artist";
import { UPDATE_ARTIST as updateArtistMutation } from "../../../../graphql/mutation/Artist";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";
import { getChangedFields, Portal } from "../../../../util";
import { FormSaveStatus, NavBar } from "../../../../components/ui";

const Body = props => {
  return (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...props} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const ArtistForm = props => {
  const { id } = useParams();
  const { data: { artist } = {} } = useQuery(
    fetchArtist({
      // Basic info fields (Infos component)
      contactId: true,
      represented: true,
      confidential: true,
      name: true,
      copyright: true,
      mailingList: true,
      droitdesuite: true,
      // Born fields (ArtistBorn component)
      bornDate: true,
      bornTown: true,
      bornCountryId: true,
      // Accounts fields (AccountsInfo component)
      sageCost: true,
      defaultFabRate: true,
      defaultSplitRate: true,
      sharedDiscount: true,
      // Consignment terms
      terms: true,
      // Contact association for ContactAddress and ContactAttributes
      contact: {
        id: true,
      },
    }),
    {
      variables: { id: +id },
    },
  );

  const [updateArtist, { loading }] = useMutation(
    updateArtistMutation({
      contact: {
        id: true,
      },
    }),
    {
      refetchQueries: [fetchArtist],
    },
  );

  const saveArtistChanges = async (values, savedValues) => {
    const changedFields = getChangedFields(values, savedValues);

    if (Object.keys(changedFields).length > 0) {
      const { data } = await updateArtist({
        variables: {
          input: {
            id: +id,
            ...changedFields,
          },
        },
      });

      if (!data?.updateArtist?.success) {
        throw new Error(
          data?.updateArtist?.error || "The artist could not be saved",
        );
      }
    }
  };

  const updateArtistValue = values => saveArtistChanges(values, artist);

  const formik = useAutosaveFormik({
    initialValues: artist,
    onSave: saveArtistChanges,
  });

  const bodyProps = {
    ...props,
    artist,
    artistId: +id,
    formik,
    loading,
    updateArtist,
    updateArtistValue,
  };

  return (
    <div className="relative flex min-h-full flex-col lg:flex-row">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="m-4 flex min-w-0 flex-1 overflow-x-auto lg:m-8 lg:mr-[80px]">
        {artist && <Body {...bodyProps} />}
      </div>
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  return <ArtistForm key={id} {...props} />;
};

export default Wizard;
